import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { SessionAuthContext } from '../components/SessionManager';
import { logger } from '../utils/logger';

/**
 * @file Provides a context for sharing team-related data.
 */

export const TeamContext = createContext(null);

/**
 * A provider component that makes the team context available to its children.
 *
 * @param {object} props - The component's props.
 * @param {React.ReactNode} props.children - The child components to render.
 * @returns {JSX.Element} The rendered TeamProvider component.
 */
export const TeamProvider = ({ children }) => {
  const session = useContext(SessionAuthContext);
  const { favoriteTeamId } = session || {};
  
  const [teamDetails, setTeamDetails] = useState(null);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);

  useEffect(() => {
    const fetchTeamDetails = async () => {
      if (!favoriteTeamId) {
        setTeamDetails(null);
        return;
      }

      // Avoid refetching if we already have the correct team details
      // This check is important to prevent unnecessary Firestore reads
      // Support both new (id) and legacy (idTeam) field names
      const currentTeamId = teamDetails?.id || teamDetails?.idTeam;
      if (currentTeamId === favoriteTeamId) {
        logger.log(`TeamProvider: Using cached team details for ID: ${favoriteTeamId}`);
        return;
      }

      setIsLoadingTeam(true);
      logger.log(`TeamProvider: Fetching details for team ID: ${favoriteTeamId}`);
      try {
        const db = getFirestore();
        const teamDocRef = doc(db, 'teams', favoriteTeamId);
        const teamDocSnap = await getDoc(teamDocRef);

        if (teamDocSnap.exists()) {
          setTeamDetails({ id: teamDocSnap.id, ...teamDocSnap.data() });
        } else {
          logger.error(`TeamProvider: No team document found for ID: ${favoriteTeamId}`);
          setTeamDetails(null);
        }
      } catch (error) {
        logger.error('TeamProvider: Error fetching team details:', error);
        setTeamDetails(null);
      } finally {
        setIsLoadingTeam(false);
      }
    };

    fetchTeamDetails();
  }, [favoriteTeamId, teamDetails?.id, teamDetails?.idTeam]); // Support both field names

  const value = useMemo(() => ({ teamDetails, isLoadingTeam }), [teamDetails, isLoadingTeam]);

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};
